import {
  GraphNodeTypes,
  NodeCategories,
  type ApiGraphData,
  type ApiNode,
  type ApiEdge,
  type RawApiGraphData,
  type RawApiNode,
  type RawApiEdge,
  type NetworkNode,
  type NetworkEdge,
  type NetworkNodeData,
  type NetworkEdgeData,
} from '../types'
import { NODE_TYPE_CONFIG, EDGE_SUBTYPE_LABELS } from '../constants'
import { applyNetworkLayout } from 'uikit/graphs/default'
import { transformMetadataForUI } from './utils'

/**
 * Normalize raw camelCase API data to snake_case ApiGraphData format.
 */
export function normalizeApiData(raw: RawApiGraphData): ApiGraphData {
  const nodes: ApiNode[] = raw.nodes.map((n: RawApiNode) => {
    let metadata: ApiNode['metadata'] | undefined
    if (n.metadata && Object.keys(n.metadata).length > 0) {
      const normalized: Record<string, unknown> = {}
      for (const [key, value] of Object.entries(n.metadata)) {
        normalized[key.replace(/([A-Z])/g, '_$1').toLowerCase()] = value
      }
      metadata = normalized as ApiNode['metadata']
    }

    return {
      id: n.id,
      identifier_key: n.identifierKey,
      type: n.type as ApiNode['type'],
      category: (n.category || 'unknown') as ApiNode['category'],
      display_name: n.displayName,
      priority: n.priority,
      position: n.position,
      metadata,
    }
  })

  const edges: ApiEdge[] = raw.edges.map((e: RawApiEdge) => ({
    source_id: e.sourceId,
    target_id: e.targetId,
    type: e.type as ApiEdge['type'],
    sub_type: e.subType as ApiEdge['sub_type'],
    external_id: e.externalId,
  }))

  return { nodes, edges }
}

/**
 * Transform API graph data to React Flow nodes and edges.
 * Preserves saved node positions; only applies layout to nodes without positions.
 */
export function transformApiToReactFlow(apiData: ApiGraphData): { nodes: NetworkNode[]; edges: NetworkEdge[] } {
  const nodes = apiData.nodes.map(apiNode => createNetworkNode(apiNode))
  const nodeIds = new Set(nodes.map(n => n.id))

  // Filter out edges that reference non-existent nodes (data integrity issue from backend)
  const validEdges = apiData.edges.filter(edge => nodeIds.has(edge.source_id) && nodeIds.has(edge.target_id))
  const edges = validEdges.map(apiEdge => createNetworkEdge(apiEdge))

  // Check if any nodes need layout (have default 0,0 position)
  const hasNodesNeedingLayout = nodes.some(node => node.position.x === 0 && node.position.y === 0)

  // Apply layout - nodes with positions stay fixed, others get positioned relative to them
  if (hasNodesNeedingLayout) {
    const positionedNodes = applyNetworkLayout(nodes, edges)
    return { nodes: positionedNodes, edges }
  }

  return { nodes, edges }
}

/**
 * Determine the React Flow node type based on category
 */
function getGraphNodeType(category: string): string {
  switch (category) {
    case NodeCategories.IDENTITY:
      return GraphNodeTypes.IDENTITY
    case NodeCategories.DATA_SOURCE:
      return GraphNodeTypes.SOURCE
    default:
      return GraphNodeTypes.CONTACT_INFO
  }
}

/**
 * Create a network node from API node data (internal helper)
 */
function createNetworkNode(apiNode: ApiNode): NetworkNode {
  // Use category from API if provided, otherwise fall back to config lookup
  const nodeCategory = apiNode.category || NODE_TYPE_CONFIG[apiNode.type]?.category || NodeCategories.UNKNOWN

  // Read position directly from node (where it's persisted)
  const savedPosition = apiNode.position

  // Nodes belonging to identity graph are not connectable (read from view_context)
  const belongsToIdentityGraph = (apiNode.view_context?.identityIds?.length ?? 0) > 0

  const data: NetworkNodeData = {
    identifierKey: apiNode.identifier_key,
    nodeType: apiNode.type,
    nodeCategory,
    displayName: apiNode.display_name,
    priority: apiNode.priority,
    isConnectable: !belongsToIdentityGraph,
    metadata: transformMetadataForUI(apiNode.metadata),
    viewContext: apiNode.view_context,
  }

  return {
    id: apiNode.id,
    type: getGraphNodeType(nodeCategory),
    position: savedPosition ?? { x: 0, y: 0 },
    data,
  }
}

/**
 * Create a network edge from API edge data
 */
function createNetworkEdge(apiEdge: ApiEdge): NetworkEdge {
  const data: NetworkEdgeData = {
    type: apiEdge.type,
    subtype: apiEdge.sub_type,
    label: EDGE_SUBTYPE_LABELS[apiEdge.sub_type] || apiEdge.sub_type,
    externalId: apiEdge.external_id,
  }

  return {
    id: `${apiEdge.source_id}-${apiEdge.target_id}-${apiEdge.sub_type}`,
    source: apiEdge.source_id,
    target: apiEdge.target_id,
    type: 'baseEdge',
    data,
  }
}
